import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { initAnalytics, identify, resetIdentity, pageview, setUserProps, setUserPropsOnce, track, EVENTS } from '../services/analytics';
import { flushStaleBuildTelemetry } from '../utils/lazyWithRetry';
import { captureLanding, captureEmailLanding, takeEmailLanding, firstTouchFacts, firstTouchPersonProps, emailMeasurementAllowed } from '../services/attribution';
import { installErrorCapture } from '../services/errorCapture';

// Per account, once the server has the first touch (recorded or already
// held). Keyed by the email so purgeLocalIdentity clears it on deletion.
const firstTouchKey = (email) => `first_touch_recorded_${email}`;

/** The first-touch facts onto the person (PostHog $set_once) and the
 * account record (#2997). Until #2997 they rode only the sign-in events, so
 * "where did this customer come from" depended on PostHog's per-device
 * $initial_* guess or a daily cache. The server keeps the first value and
 * refuses an account that does not exist yet; a refusal leaves the key
 * unset, so the next page load tries again. */
export function persistFirstTouch(email) {
    const facts = firstTouchFacts();
    if (!facts.landing_path) return;
    setUserPropsOnce(firstTouchPersonProps(facts));
    let done = false;
    try { done = localStorage.getItem(firstTouchKey(email)) === '1'; } catch { /* storage blocked */ }
    if (done) return;
    import('../services/api').then(({ recordFirstTouch }) => recordFirstTouch(email, facts))
        .then((res) => {
            // Settled answers stop the retries; only no_account / a failed
            // write (or network) leaves the key unset for the next load.
            if (res?.success && (res.recorded || ['already_recorded', 'nothing_to_record', 'opted_out'].includes(res.reason))) {
                try { localStorage.setItem(firstTouchKey(email), '1'); } catch { /* storage blocked */ }
            }
        })
        .catch(() => { /* best effort: retried next load */ });
}

// One place that drives all passive analytics (no-op until VITE_POSTHOG_KEY is
// set). Mounted inside the Router + AuthProvider so it can see both:
//   - init once on mount
//   - a $pageview on every route change (SPA — the browser fires none)
//   - identify on sign-in, reset on sign-out (funnels/retention need a stable id)
// Renders nothing.
export default function AnalyticsListener() {
    const location = useLocation();
    const { currentUser } = useAuth();
    const lastEmail = useRef(null);

    // Capture first-touch landing (which video/paper + campaign params) BEFORE
    // any sign-in can complete, so the sign_up conversion carries "which proof
    // converted." First touch wins and survives the mobile OAuth redirect.
    useEffect(() => { captureLanding(); captureEmailLanding(); initAnalytics(); installErrorCapture(); }, []);

    // If the previous page load ended in a stale-build auto-reload, report it
    // now — the event could not be sent then, because the reload fires
    // synchronously. TELEMETRY ONLY: this must never restore the retry budget.
    useEffect(() => {
        flushStaleBuildTelemetry((chunk) => track(EVENTS.STALE_BUILD_RELOADED, { chunk }));
    }, []);

    useEffect(() => {
        pageview(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        const email = currentUser?.email || null;
        if (!email) {
            if (lastEmail.current) {
                resetIdentity();   // signed out — don't merge the next user into this one
                lastEmail.current = null;
            }
            return;
        }
        if (email === lastEmail.current) return;
        identify(email);
        setUserProps({ signup_domain: email.split('@')[1] || null });
        persistFirstTouch(email);
        lastEmail.current = email;
        // An emailed link opened (#2408): fired AFTER identify so the click
        // lands on this person's hashed id; the campaign id only, never PII.
        // Per-address email measurement starts on the day the Privacy Policy
        // that discloses it went live (EMAIL_MEASUREMENT_STARTS); before it
        // the held landing is consumed and dropped, never tied to the person.
        const landed = takeEmailLanding();
        if (landed && emailMeasurementAllowed()) track(EVENTS.EMAIL_LINK_LANDED, landed);
    }, [currentUser?.email]);

    return null;
}
